import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import { FormattedMessage } from 'react-intl';
import useOnScreen from 'src/hooks/useOnScreen';
import drugCartTrns from 'src/shared-components/DrugCard/trns';
import { useRouter } from 'next/router';
import { isNotInStock } from 'src/utils/drugHelper';
import useModalStore from 'src/hooks/useModalStore';
import SelectorComponent from 'src/shared-components/CitySelect/SelectorComponent';
import { useAnalytics } from 'src/hooks/useAnalytics/useAnalytics';
import useGTAG, { GTAGPrefix } from 'src/hooks/useGTAG';
import ButtonSecondary, {
  ButtonSecondarySize
} from 'src/shared-components/design-system/ButtonSecondary';
import useLocationStore from 'src/hooks/useLocationStore';
import List from 'src/icons/List';
import useOneDrugOrderStore from 'src/hooks/useOneDrugOrderStore';

import useCartStore from '../../../../hooks/useCart';
import DrugImage from '../../../../shared-components/DrugImage/DrugImage';
import trns from '../../trns';
import { AllAnalogues, DrugItem } from '../../../../api/types';
import buttonsTrns from '../../../../shared-components/Button/trns';
import DescriptionTabContent from './DescriptionTabContent';
import InstructionsTabContent from './InstructionsTabContent';
import AnalogueTabContent from './AnalogueTabContent';
import { TabTypes } from '../../utils';
import { DrugInCartComponent } from '../DrugInCartComponent';
import UnitCounter from '../../../../shared-components/UnitCounter/UnitCounter';
import WarningMessageBlock from './WarningMessageBlock';
import Button from '../../../../shared-components/design-system/ButtonTertiary';
import BellIcon from '../../../../icons/Bell';
import ButtonPrimary, {
  ButtonPrimarySize
} from '../../../../shared-components/design-system/ButtonPrimary';

interface Props {
  className?: string;
  drug: DrugItem;
  thirdCategoryName: string;
  tabId: TabTypes;
  analoguesList: AllAnalogues | null;
  gtagPrefix?: GTAGPrefix;
}

interface DefaultTabContentProps extends Props {
  className?: string;
}

const DefaultTabContent: FC<DefaultTabContentProps> = ({
  drug,
  thirdCategoryName,
  className,
  gtagPrefix
}) => {
  const {
    id,
    translitName,
    name,
    foundInBranchesCount,
    maxPrice,
    minPrice,
    imageExists
  } = drug;
  const bookingPriceRef = useRef(null);
  const buttonRef = useRef(null);

  const router = useRouter();

  const { updateModalsConfig, addNotificationModal } = useModalStore();
  const { addItemToCart, drugInCart, addItemToSingleDrugCart } = useCartStore();
  const { userLocation } = useLocationStore();
  const { setOneDrugOrder, setBackRoute } = useOneDrugOrderStore();
  const { sendChangeCartItemsAnalytic } = useAnalytics();
  const { trackGtagEvent } = useGTAG();

  const isShowBookingButton = useOnScreen(bookingPriceRef);
  const notInStock = isNotInStock(minPrice, foundInBranchesCount);
  const [quantity, setQuantity] = useState({
    packQuantity: 1,
    unitQuantity: 0
  });

  const isDrugInCart = drugInCart(translitName);

  const handleAddItemToList = (drugParam: DrugItem) => {
    addItemToCart({
      drug: drugParam,
      packQuantity: 1,
      unitQuantity: 0,
      gtagPrefix: GTAGPrefix.item
    });
    setTimeout(() => {
      sendChangeCartItemsAnalytic({
        drug: drugParam,
        packageQuantityDetails: { packQuantity: 1 }
      });
      trackGtagEvent(`${gtagPrefix}_cart`);
    });
  };

  const handleQuantityChange = async (packQuantity, unitQuantity) => {
    setQuantity({ packQuantity, unitQuantity });
    return Promise.resolve();
  };

  const redirectToSelectTheShop = useCallback(
    (drugParam: DrugItem) => {
      router.push('/checkout/cart?step=proposalSelect', undefined, {
        shallow: true
      });
      setTimeout(() => {
        addItemToSingleDrugCart({
          drug: drugParam,
          packQuantity: quantity.packQuantity,
          unitQuantity: quantity.unitQuantity,
          gtagPrefix: GTAGPrefix.item
        }).then(() =>
          sendChangeCartItemsAnalytic({
            drug: drugParam,
            packageQuantityDetails: {
              ...quantity,
              quantityUnitInPack: drugParam.quantityUnitInPack
            }
          })
        );
        trackGtagEvent(`${gtagPrefix}_pharmacy_choose`);
        setBackRoute(router.asPath);
        setOneDrugOrder(true);
      });
    },
    [addItemToSingleDrugCart]
  );

  const redirectToMap = (drugParam: DrugItem) => {
    router.push('/checkout/cart?step=proposalMap', undefined, {
      shallow: true
    });
    setTimeout(() => {
      addItemToSingleDrugCart({
        drug: drugParam,
        packQuantity: quantity.packQuantity,
        unitQuantity: quantity.unitQuantity
      }).then(() =>
        sendChangeCartItemsAnalytic({
          drug: drugParam,
          packageQuantityDetails: {
            ...quantity,
            quantityUnitInPack: drugParam.quantityUnitInPack
          }
        })
      );
      setBackRoute(router.asPath);
      setOneDrugOrder(true);
    });
  };

  useEffect(() => {
    setQuantity({
      packQuantity: 1,
      unitQuantity: 0
    });
  }, [drug?.translitName]);

  useEffect(() => {
    if (drug) {
      addNotificationModal({
        id: drug.id,
        translitName: drug.translitName,
        name: drug.name,
        gtagPrefix: GTAGPrefix.default
      });
    }
  }, [drug?.translitName, addNotificationModal]);

  return (
    <div id="allInfo" className={className}>
      <div className="lm:flex items-stretch flex-column px-[18px] lm:px-0 mb-7 lm:mb-0">
        <div className="flex flex-col h-full items-center justify-start overflow-hidden lm:mr-[60px]">
          <div
            className={cn(
              'max-w-[100vw] w-full m:w-[400px] h-[300px] m:h-[400px] relative'
            )}
          >
            <DrugImage
              layout="responsive"
              objectFit="contain"
              unoptimized
              drugId={id}
              size="L"
              fill
              drugName={name}
              priority
              imageExists={imageExists}
            />
          </div>
        </div>

        <div className="flex flex-col justify-center">
          {notInStock ? (
            <>
              <p className="fs-title3 text-astronautBlue lm:mb-[40%] pt-4 pb-5 uppercase">
                <FormattedMessage {...trns.notExist} />
              </p>
              <div className="w-[204px]">
                <Button
                  onClick={() =>
                    updateModalsConfig(
                      `notification-modal-${translitName}`,
                      true
                    )
                  }
                  buttonLabel={
                    <FormattedMessage {...drugCartTrns.notifyAvailable} />
                  }
                  icon={{
                    alt: 'bell',
                    component: BellIcon
                  }}
                />
              </div>
            </>
          ) : (
            <>
              <p className="flex items-center">
                <span className="fs-body-reg mr-1 text-darkBlue">
                  <FormattedMessage {...trns.priceInCity} />{' '}
                </span>
                <SelectorComponent
                  ref={buttonRef}
                  selectorClassName="text-astronautBlue fs-body-bold mr-9"
                  onClick={() => {
                    trackGtagEvent(
                      `${gtagPrefix}_city`,
                      userLocation?.name ?? '',
                      'city'
                    );
                  }}
                />
              </p>
              <p className="text-astronautBlue pt-4 pb-5 text-[15px] uppercase">
                <FormattedMessage {...trns.foundIn} />{' '}
                <span className="inline">
                  <FormattedMessage
                    {...trns.foundInStoresLabel}
                    values={{
                      count: (
                        <button
                          onClick={() => {
                            redirectToSelectTheShop(drug);
                          }}
                        >
                          <span>{foundInBranchesCount}</span>
                        </button>
                      )
                    }}
                  />
                </span>
                :{' '}
              </p>
              <p
                className="text-vermilion text-[32px] pb-16"
                ref={bookingPriceRef}
              >
                <span className="fs-body-reg text-darkBlue">
                  <FormattedMessage {...trns.from} />{' '}
                </span>
                {`${(Math.round(minPrice * 100) / 100)?.toFixed(2)} ₴`}{' '}
                <span className="fs-body-reg text-darkBlue">
                  <FormattedMessage {...trns.to} />{' '}
                </span>
                {(Math.round(maxPrice * 100) / 100)?.toFixed(2)} ₴
              </p>
              {!notInStock && (
                <div className="mb-7">
                  <UnitCounter
                    drug={drug}
                    packQuantity={quantity.packQuantity}
                    unitQuantity={quantity.unitQuantity}
                    onQuantityChange={handleQuantityChange}
                    gtagPrefix={gtagPrefix}
                  />
                </div>
              )}
              <div className="flex flex-col s:flex-row">
                <div className="flex items-center justify-between mb-3 s:mb-0">
                  <ButtonPrimary
                    disabled={
                      quantity.packQuantity === 0 && quantity.unitQuantity === 0
                    }
                    onClick={() => {
                      redirectToSelectTheShop(drug);
                    }}
                    className="w-full mr-3"
                    data-cy="select_the_shop_button"
                    size={ButtonPrimarySize.xl}
                    label={
                      <span className="uppercase">
                        <FormattedMessage {...trns.selectTheShop} />
                      </span>
                    }
                  />
                  <ButtonSecondary
                    disabled={
                      quantity.packQuantity === 0 && quantity.unitQuantity === 0
                    }
                    onClick={() => {
                      redirectToMap(drug);
                    }}
                    className="w-full s:hidden"
                    data-cy="select_the_map_button"
                    size={ButtonSecondarySize.xl}
                    label={
                      <span className="fs-body-bold">
                        <FormattedMessage {...trns.openMapLabel} />
                      </span>
                    }
                  />
                </div>
                {isDrugInCart ? (
                  <DrugInCartComponent />
                ) : (
                  <Button
                    onClick={() => {
                      handleAddItemToList(drug);
                    }}
                    data-cy="add_to_cart_button"
                    disabled={
                      quantity.packQuantity === 0 && quantity.unitQuantity === 0
                    }
                    buttonLabel={
                      <FormattedMessage {...buttonsTrns.toListButton} />
                    }
                    icon={{
                      alt: 'list',
                      component: List
                    }}
                  />
                )}
              </div>
            </>
          )}
        </div>
      </div>
      {/* Fixed footer bar on mobile screen */}
      <div
        className={cn(
          'fixed lm:hidden w-full bottom-0 z-70 bg-white flex flex-col p-[19px] items-center',
          {
            hidden: isShowBookingButton || notInStock
          }
        )}
      >
        {isDrugInCart ? (
          <div className="flex items-center ml-auto">
            <DrugInCartComponent />
          </div>
        ) : (
          <>
            <div className="flex w-full items-center justify-between mb-3">
              <ButtonPrimary
                onClick={() => {
                  redirectToSelectTheShop(drug);
                }}
                className="w-full mr-3"
                data-cy="select_the_shop_button_mobile"
                size={ButtonPrimarySize.xl}
                label={
                  <span className="uppercase">
                    <FormattedMessage {...trns.selectTheShop} />
                  </span>
                }
              />
              <ButtonSecondary
                onClick={() => {
                  redirectToMap(drug);
                }}
                className="w-full"
                data-cy="select_the_map_button_mobile"
                size={ButtonSecondarySize.xl}
                label={
                  <span className="fs-body-bold">
                    <FormattedMessage {...trns.openMapLabel} />
                  </span>
                }
              />
            </div>
            <Button
              onClick={() => {
                handleAddItemToList(drug);
              }}
              className="w-full"
              data-cy="add_to_cart_button_mobile"
              buttonLabel={<FormattedMessage {...buttonsTrns.toListButton} />}
              icon={{
                alt: 'list',
                component: List
              }}
            />
          </>
        )}
      </div>
      <div id={TabTypes.CHARACTERISTICS} className="mb-12 lm:mb-[60px]">
        <DescriptionTabContent
          drug={drug}
          thirdCategoryName={thirdCategoryName}
          gtagPrefix={gtagPrefix}
        />
      </div>
      <WarningMessageBlock />
      <div id={TabTypes.INSTRUCTIONS} className="mx-[19px]">
        <InstructionsTabContent
          drug={drug}
          thirdCategoryName={thirdCategoryName}
          gtagPrefix={gtagPrefix}
        />
      </div>
    </div>
  );
};

const AboutProduct: FC<Props> = ({
  drug,
  thirdCategoryName,
  tabId,
  analoguesList,
  gtagPrefix
}) => {
  const analogSelected = (tabId as TabTypes) === TabTypes.ANALOGUE;

  return (
    <>
      <AnalogueTabContent
        drug={drug}
        thirdCategoryName={thirdCategoryName}
        analoguesList={analoguesList}
        className={cn({
          invisible: !analogSelected,
          'h-0': !analogSelected
        })}
      />
      {!analogSelected && (
        <DefaultTabContent
          drug={drug}
          thirdCategoryName={thirdCategoryName}
          tabId={tabId}
          analoguesList={analoguesList}
          gtagPrefix={gtagPrefix}
        />
      )}
    </>
  );
};

export default AboutProduct;
